import { ASSISTENT_API_URL } from '@/config/backend'
import { logger } from '@/lib/logger'
import axios from 'axios'
import { create } from 'zustand'
import { useAuthStore } from './auth-store'
import { useDevicesStore } from './devices-store'

export interface AnswerCategory {
  id: string
  type: 'ANSWER' | 'OPTION'
  name: string
  title: string
  description?: string
}

export interface AnswerVariant {
  id: string
  content: string
  defaultAnswer?: boolean
}

export interface Answer {
  id: string
  title?: string
  guestMessage?: string
  aiResponse?: string
  notes?: string
  editableByCustomer?: boolean
  smsEnabled?: boolean
  forwardingEnabled?: boolean
  active?: boolean
  priority?: number
  // ANSWER type fields
  categoryId?: number
  exampleConversation?: string
  visibleByCustomer?: boolean
  overriden?: boolean
  answerVariants?: AnswerVariant[]
  // OPTION type fields  
  displayTitle?: string
  defaultAnswerActive?: string
  defaultAnswerInactive?: string
  customerAnswerActive?: string | null
  customerAnswerInactive?: string | null
  enabled?: boolean
  // Legacy properties for compatibility
  guestSaid?: string
  text?: string
  description?: string
  lastModified?: Date
  order?: number
}

export interface CallHandlingProfile {
  id: string
  title: string
  name?: string
  phoneNumber?: string
  description?: string
  active: boolean
  errorhandler: boolean
  messageActive: string
  messageInactive: string
  receivers: string[]
  timeslots?: Record<string, any[]>
  usedByActions: string[]
}

export interface ForwardRecipient {
  id: string
  title: string
  forwardingNumber: string
  active: boolean
}

export type ForwardViewType = 'none' | 'profile' | 'recipient'

interface CustomerState {
  // Forward selection state
  selectedForwardProfileId: string | null
  selectedForwardRecipientId: string | null
  forwardViewType: ForwardViewType
  answerCategories: AnswerCategory[]
  currentCategory: AnswerCategory | null
  currentAnswersByCategory: Record<string, Answer[]>
  answersByCategory: Record<string, Answer[]>
  callhandlingProfiles: CallHandlingProfile[]
  forwardRecipients: ForwardRecipient[]
  processing: boolean
  globalProcessing: boolean

  // Actions
  readCategories: () => Promise<void>
  readAllData: () => Promise<void>
  loadCategoryData: () => Promise<void>
  loadCategories: () => Promise<void>
  loadCallHandlingProfiles: () => Promise<void>
  loadForwardRecipients: () => Promise<void>
  selectForwardProfile: (profileId: string | null) => void
  selectForwardRecipient: (recipientId: string | null) => void
  clearForwardSelection: () => void
  createCallHandlingProfile: (profile: Partial<CallHandlingProfile>) => Promise<void>
  updateCallHandlingProfile: (profile: CallHandlingProfile) => Promise<void>
  deleteCallHandlingProfile: (profileId: string) => Promise<void>
  setCategoryById: (categoryId: string) => void
  getCategory: (categoryId: string) => AnswerCategory | null
  setCategory: (category: AnswerCategory) => Promise<void> | void
  setCurrentCategory: (category: AnswerCategory) => void
  setFirstCategory: () => void
  refreshCategory: (requestedCategoryId: string) => Promise<void>
  loadAnswersByCategory: (categoryId: string) => Promise<void>
  updateAnswer: (answer: Answer, customerOverridenAnswerText?: string, useSystemDefault?: boolean) => Promise<void>
  loadSystemAnswers: (answerId: string) => Promise<any>
  createAnswer: (answer: Partial<Answer>) => Promise<void>
  deleteAnswer: (answerId: string) => Promise<void>
  getCurentAnswers: () => Answer[]
  getAllAnswers: () => Answer[]
  getAnswersByCategory: (category: AnswerCategory) => Answer[]
  getAnswerCountForCategory: (category: AnswerCategory) => number
  getFirstCategoryWithAnswers: () => AnswerCategory | null
  reset: () => Promise<void>
  resetCallhandlingProfiles: () => Promise<void>
  getActionByTitle: (actionTitle: string) => Answer | null
  isActionEditable: (actionTitle: string) => boolean
  updateAnswerSmsMode: (actionTitle: string, newMode: boolean) => void
  updateAnswerForwardingMode: (actionTitle: string, newMode: boolean) => void
  findCallHandlingProfileForAnswer: (answerId: string) => CallHandlingProfile | null
  isProcessing: () => boolean
}

// Store integration helpers
const getAuthHeaders = () => {
  return useAuthStore.getState().getApiHeaders()
}

const getCurrentDeviceSerialId = () => {
  return useDevicesStore.getState().getCurrentDeviceSerialId() || ''
}

const isDeviceSelected = () => {
  return useDevicesStore.getState().isDeviceSelected()
}

const isCustomer = () => {
  return useAuthStore.getState().isCustomer()
}

const isMdsDevice = () => {
  return useDevicesStore.getState().isMdsDevice()
}

const getLanguage = () => {
  return localStorage.getItem('language') || 'de'
}

const showAlertByKey = (key: string) => {
  console.error('Alert:', key)
  // This will be replaced with proper toast notifications
}

export const useCustomerStore = create<CustomerState>((set, get) => ({
  // Forward selection state
  selectedForwardProfileId: null,
  selectedForwardRecipientId: null,
  forwardViewType: 'none' as ForwardViewType,

  answerCategories: [],
  currentCategory: null,
  currentAnswersByCategory: {},
  answersByCategory: {},
  callhandlingProfiles: [],
  forwardRecipients: [],
  processing: false,
  globalProcessing: false,

  readCategories: async () => {
    const state = get()
    if (isDeviceSelected() && (state.answerCategories.length === 0)) {
      const url = `${ASSISTENT_API_URL}answers/${getCurrentDeviceSerialId()}/categories/${getLanguage()}`

      set({ processing: true })

      try {
        const response = await axios.get(url, {
          headers: getAuthHeaders()
        })

        if (response.data.errorCode !== null) {
          showAlertByKey(response.data.errorCode)
          set({ answerCategories: [], processing: false })
        } else {
          const categories = Object.values(response.data.resultObject.categories) as AnswerCategory[]
          console.log('📚 CATEGORIES: Loaded categories:', categories.map(c => ({ id: c.id, name: c.name, title: c.title, type: c.type })))
          set({ answerCategories: categories })

          await get().loadCategoryData()

          // Log categories with answer counts
          const { currentAnswersByCategory } = get()
          console.log('📊 CATEGORIES: Answer counts:', Object.entries(currentAnswersByCategory).map(([id, answers]) => {
            const cat = categories.find(c => c.id === id)
            return { id, name: cat?.name || cat?.title, count: answers.length }
          }))

          get().setFirstCategory()
          set({ processing: false })
        }
      } catch (error) {
        console.error('Failed to load categories:', error)
        set({ answerCategories: [], processing: false })
        // Don't throw - just log the error and continue with empty categories
      }
    }
  },

  readAllData: async () => {
    const { readCategories, loadCategoryData, setFirstCategory, loadCallHandlingProfiles } = get()
    await readCategories()
    await loadCategoryData()
    setFirstCategory()
    await loadCallHandlingProfiles()
  },

  loadCategoryData: async () => {
    const { answerCategories, currentAnswersByCategory, refreshCategory } = get()
    for (const cat of answerCategories) {
      if (!currentAnswersByCategory[cat.id]) {
        try {
          await refreshCategory(cat.id)
        } catch (error) {
          // Skip categories that fail to load (e.g., 404)
          console.warn(`Failed to load category ${cat.id}:`, error)
        }
      }
    }
  },

  loadCallHandlingProfiles: async () => {
    const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/profiles`

    try {
      const response = await axios.get(url, {
        headers: getAuthHeaders()
      })
      const profiles = response.data.resultObject.profiles || []
      console.log('📋 Loaded basic profiles:', profiles.length)
      console.log('📋 First profile sample:', JSON.stringify(profiles[0], null, 2))

      // Transform timeslots from API format to frontend format
      const transformedProfiles = profiles.map((profile: any) => {
        // Ensure receivers is always an array
        if (!profile.receivers || !Array.isArray(profile.receivers)) {
          profile.receivers = []
        }

        if (profile.timeslots && typeof profile.timeslots === 'object') {
          // API format has keys like "MONDAY", "TUESDAY" etc.
          // Transform each timeslot in each day
          const transformedTimeslots: Record<string, any[]> = {}

          Object.entries(profile.timeslots).forEach(([day, slots]: [string, any]) => {
            if (Array.isArray(slots)) {
              transformedTimeslots[day] = slots.map((slot: any) => ({
                id: slot.id,
                receiverId: slot.receiver?.id || slot.receiverId,
                dayOfWeek: day,
                startTime: slot.timeStart || slot.startTime,
                endTime: slot.timeEnd || slot.endTime
              }))
            }
          })

          profile.timeslots = transformedTimeslots
          console.log(`✅ Transformed timeslots for "${profile.title}":`, transformedTimeslots)
        }

        return profile
      })

      console.log('✅ All profiles loaded with timeslots:', transformedProfiles.map((p: any) => ({
        title: p.title,
        hasTimeslots: !!p.timeslots,
        timeslotDays: p.timeslots ? Object.keys(p.timeslots) : []
      })))

      set({ callhandlingProfiles: transformedProfiles })
    } catch (error) {
      console.error('❌ Failed to load profiles:', error)
      showAlertByKey("error.unknown")
    }
  },

  loadForwardRecipients: async () => {
    const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/receivers`

    try {
      const response = await axios.get(url, {
        headers: getAuthHeaders()
      })
      const recipients = response.data.resultObject.receivers || []
      set({ forwardRecipients: recipients })
    } catch (error) {
      console.error('❌ Failed to load recipients:', error)
      set({ forwardRecipients: [] })
    }
  },

  selectForwardProfile: (profileId: string | null) => {
    set({
      selectedForwardProfileId: profileId,
      selectedForwardRecipientId: null,
      forwardViewType: profileId ? 'profile' : 'none'
    })
  },

  selectForwardRecipient: (recipientId: string | null) => {
    set({
      selectedForwardProfileId: null,
      selectedForwardRecipientId: recipientId,
      forwardViewType: recipientId ? 'recipient' : 'none'
    })
  },

  clearForwardSelection: () => {
    set({
      selectedForwardProfileId: null,
      selectedForwardRecipientId: null,
      forwardViewType: 'none'
    })
  },

  setCategoryById: (categoryId: string) => {
    const { currentCategory, getCategory, setCategory } = get()
    if (!currentCategory || currentCategory.id !== categoryId) {
      const cat = getCategory(categoryId)
      if (cat) {
        setCategory(cat)
      }
    }
  },

  getCategory: (categoryId: string) => {
    const { answerCategories } = get()
    return answerCategories.find(item => item.id === categoryId) || null
  },

  setCategory: (category: AnswerCategory) => {
    const { currentCategory, currentAnswersByCategory, refreshCategory } = get()

    if (currentCategory?.id === category.id) {
      set({ currentCategory: null, processing: false })
    } else if (currentAnswersByCategory[category.id]) {
      set({ currentCategory: category, processing: false })
    } else {
      set({ currentCategory: category })
      return refreshCategory(category.id)
    }
  },

  setFirstCategory: () => {
    const { answerCategories, getFirstCategoryWithAnswers } = get()

    if (answerCategories.length === 1) {
      set({ currentCategory: answerCategories[0] })
    } else if (answerCategories.length > 1) {
      const firstCategoryWithAnswers = getFirstCategoryWithAnswers()
      set({ currentCategory: firstCategoryWithAnswers })
    } else {
      set({ currentCategory: null })
    }
  },

  refreshCategory: async (requestedCategoryId: string) => {
    const { getCategory, currentAnswersByCategory } = get()
    const category = getCategory(requestedCategoryId)

    if (!category) return

    set({ processing: true })

    try {
      let url: string
      if (category.type === 'ANSWER') {
        url = `${ASSISTENT_API_URL}answers/${getCurrentDeviceSerialId()}/${requestedCategoryId}`
      } else {
        url = `${ASSISTENT_API_URL}answers/options/${getCurrentDeviceSerialId()}/${requestedCategoryId}`
      }

      const response = await axios.get(url, {
        headers: getAuthHeaders()
      })

      if (response.data.errorCode !== null) {
        showAlertByKey(response.data.errorCode)
        // Set empty array for categories with errors
        set({
          currentAnswersByCategory: {
            ...currentAnswersByCategory,
            [requestedCategoryId]: []
          },
          processing: false
        })
      } else {
        const answers = category.type === 'ANSWER'
          ? response.data.resultObject.answerTypes
          : response.data.resultObject.optionTypes

        set({
          currentAnswersByCategory: {
            ...currentAnswersByCategory,
            [requestedCategoryId]: answers
          }
        })
        set({ processing: false })
      }
    } catch (error: any) {
      // On 404 or other errors, set empty array and continue
      console.warn(`Failed to load category ${requestedCategoryId}:`, error.response?.status, error.message)
      set({
        currentAnswersByCategory: {
          ...currentAnswersByCategory,
          [requestedCategoryId]: []
        },
        processing: false
      })
    }
  },

  getCurentAnswers: () => {
    const { currentCategory, currentAnswersByCategory } = get()
    if (currentCategory?.id && currentAnswersByCategory[currentCategory.id]) {
      return currentAnswersByCategory[currentCategory.id]
    }
    return []
  },

  getAllAnswers: () => {
    const { currentAnswersByCategory } = get()
    return Object.values(currentAnswersByCategory).flat()
  },

  getAnswersByCategory: (category: AnswerCategory) => {
    const { currentAnswersByCategory } = get()
    return currentAnswersByCategory[category.id] || []
  },

  getAnswerCountForCategory: (category: AnswerCategory) => {
    const { currentAnswersByCategory } = get()
    return currentAnswersByCategory[category.id]?.length || 0
  },

  getFirstCategoryWithAnswers: () => {
    const { answerCategories, getAnswerCountForCategory } = get()
    for (const category of answerCategories) {
      if (getAnswerCountForCategory(category) > 0) {
        return category
      }
    }
    return null
  },

  reset: async () => {
    const { readAllData } = get()

    logger.customer('Reset called', {
      deviceSelected: isDeviceSelected(),
      isCustomer: isCustomer(),
      isMds: isMdsDevice()
    })

    set({
      globalProcessing: true,
      answerCategories: [],
      processing: false,
      currentCategory: null,
      currentAnswersByCategory: {}
    })

    if (isDeviceSelected() && isCustomer() && !isMdsDevice()) {
      logger.customer('Loading all customer data')
      await readAllData()
      logger.customer('Data loaded successfully')
    } else {
      logger.customer('Skipping data load (MDS device or not customer)')
    }
    set({ globalProcessing: false })
  },

  resetCallhandlingProfiles: async () => {
    const { loadCallHandlingProfiles } = get()
    set({ globalProcessing: true, callhandlingProfiles: [] })
    await loadCallHandlingProfiles()
    set({ globalProcessing: false })
  },

  getActionByTitle: (actionTitle: string) => {
    const { currentAnswersByCategory } = get()
    const actionList = Object.values(currentAnswersByCategory).flat()
    return actionList.find(item => item.id === actionTitle) || null
  },

  isActionEditable: (actionTitle: string) => {
    const { getActionByTitle } = get()
    const action = getActionByTitle(actionTitle)
    return action?.editableByCustomer || false
  },

  updateAnswerSmsMode: (actionTitle: string, newMode: boolean) => {
    const { getActionByTitle } = get()
    const action = getActionByTitle(actionTitle)
    if (action) {
      action.smsEnabled = newMode
    }
  },

  updateAnswerForwardingMode: (actionTitle: string, newMode: boolean) => {
    const { getActionByTitle } = get()
    const action = getActionByTitle(actionTitle)
    if (action) {
      action.forwardingEnabled = newMode
    }
  },

  findCallHandlingProfileForAnswer: (answerId: string) => {
    const { callhandlingProfiles } = get()
    return callhandlingProfiles.find(
      (p) =>
        p.usedByActions &&
        p.usedByActions.some((a) => a === answerId)
    ) || null
  },

  loadCategories: async () => {
    await get().readCategories()
  },

  setCurrentCategory: (category: AnswerCategory) => {
    set({ currentCategory: category })
  },

  loadAnswersByCategory: async (categoryId: string) => {
    await get().refreshCategory(categoryId)
  },

  updateAnswer: async (answer: Answer, customerOverridenAnswerText?: string, useSystemDefault?: boolean) => {
    const { currentCategory } = get()
    const categoryId = currentCategory?.id

    if (!categoryId) {
      throw new Error('No category selected')
    }

    try {
      const { getCurrentDeviceSerialId } = useDevicesStore.getState()
      const headers = useAuthStore.getState().getApiHeaders()

      // Determine endpoint and body based on answer type
      const isAnswerType = !!answer.answerVariants
      let url: string
      let putBody: any

      if (isAnswerType) {
        // ANSWER type: Use override endpoint
        url = `${ASSISTENT_API_URL}answers/${getCurrentDeviceSerialId()}/${answer.id}`
        putBody = {
          customerOverridenAnswerText: customerOverridenAnswerText || '',
          useSystemDefault: useSystemDefault !== undefined ? useSystemDefault : true
        }
      } else {
        // OPTION type: Use options endpoint (URL: answers/options/{deviceId}/{categoryId}/{optionId})
        url = `${ASSISTENT_API_URL}answers/options/${getCurrentDeviceSerialId()}/${categoryId}/${answer.id}`
        putBody = {
          enabled: answer.enabled,
          answerActiveOverriden: null,
          answerInactiveOverriden: null,
        }
        // If customer text is provided, set the appropriate override field based on enabled state
        if (customerOverridenAnswerText && !useSystemDefault) {
          if (answer.enabled) {
            putBody.answerActiveOverriden = customerOverridenAnswerText
          } else {
            putBody.answerInactiveOverriden = customerOverridenAnswerText
          }
        }
      }

      const response = await axios.put(url, putBody, { headers })

      if (response.data.errorCode !== null) {
        throw new Error(response.data.errorCode)
      }

      // Reload the category to get updated data from server
      await get().refreshCategory(categoryId)

    } catch (error) {
      console.error('Failed to update answer:', error)
      throw error
    }
  },

  loadSystemAnswers: async (answerId: string) => {
    try {
      const { getCurrentDeviceSerialId } = useDevicesStore.getState()
      const headers = useAuthStore.getState().getApiHeaders()

      const url = `${ASSISTENT_API_URL}answers/${getCurrentDeviceSerialId()}/systemanswers/${answerId}`

      const response = await axios.get(url, { headers })

      if (response.data.errorCode !== null) {
        throw new Error(response.data.errorCode)
      }

      return response.data.resultObject
    } catch (error) {
      console.error('Failed to load system answers:', error)
      throw error
    }
  }, createAnswer: async (answer: Partial<Answer>) => {
    const { currentAnswersByCategory, currentCategory } = get()
    const categoryId = currentCategory?.id

    if (!categoryId) {
      throw new Error('No category selected')
    }

    try {
      const { getCurrentDeviceSerialId } = useDevicesStore.getState()
      const headers = useAuthStore.getState().getApiHeaders()

      // Determine the correct endpoint based on category type
      let url: string
      if (currentCategory.type === 'ANSWER') {
        url = `${ASSISTENT_API_URL}answers/${getCurrentDeviceSerialId()}/${categoryId}`
      } else {
        url = `${ASSISTENT_API_URL}answers/options/${getCurrentDeviceSerialId()}/${categoryId}`
      }

      // Send create to backend
      const response = await axios.post(url, answer, { headers })

      if (response.data.errorCode !== null) {
        throw new Error(response.data.errorCode)
      }

      // Get the created answer from response (with server-generated ID)
      const createdAnswer: Answer = response.data.resultObject

      // Update local state with server response
      if (currentAnswersByCategory[categoryId]) {
        const answers = [...currentAnswersByCategory[categoryId], createdAnswer]
        set({
          currentAnswersByCategory: {
            ...currentAnswersByCategory,
            [categoryId]: answers
          },
          answersByCategory: {
            ...currentAnswersByCategory,
            [categoryId]: answers
          }
        })
      }
    } catch (error) {
      console.error('Failed to create answer:', error)
      throw error
    }
  },

  deleteAnswer: async (answerId: string) => {
    const { currentAnswersByCategory, currentCategory } = get()
    const categoryId = currentCategory?.id

    if (!categoryId || !currentAnswersByCategory[categoryId]) {
      throw new Error('No category selected')
    }

    try {
      // Call API to delete answer
      const { getCurrentDeviceSerialId } = useDevicesStore.getState()
      const headers = useAuthStore.getState().getApiHeaders()
      const url = `${ASSISTENT_API_URL}answers/${getCurrentDeviceSerialId()}/${categoryId}/${answerId}`

      await axios.delete(url, { headers })

      // Remove from local state
      const answers = currentAnswersByCategory[categoryId].filter(a => a.id !== answerId)
      set({
        currentAnswersByCategory: {
          ...currentAnswersByCategory,
          [categoryId]: answers
        },
        answersByCategory: {
          ...currentAnswersByCategory,
          [categoryId]: answers
        }
      })
    } catch (error) {
      console.error('Failed to delete answer:', error)
      throw error
    }
  },

  createCallHandlingProfile: async (profile: Partial<CallHandlingProfile>) => {
    try {
      const { getCurrentDeviceSerialId } = useDevicesStore.getState()
      const headers = useAuthStore.getState().getApiHeaders()
      const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/profiles`

      const response = await axios.post(url, {
        name: profile.name,
        phoneNumber: profile.phoneNumber,
        description: profile.description,
        active: profile.active ?? true
      }, { headers })

      // Reload profiles after creation
      await get().loadCallHandlingProfiles()

      return response.data
    } catch (error) {
      console.error('Failed to create call handling profile:', error)
    }
  },

  updateCallHandlingProfile: async (profile: CallHandlingProfile) => {
    try {
      const { getCurrentDeviceSerialId } = useDevicesStore.getState()
      const headers = useAuthStore.getState().getApiHeaders()
      const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/profiles/${profile.id}`

      await axios.put(url, {
        name: profile.name,
        phoneNumber: profile.phoneNumber,
        description: profile.description,
        active: profile.active
      }, { headers })

      // Reload profiles after update
      await get().loadCallHandlingProfiles()
    } catch (error) {
      console.error('Failed to update call handling profile:', error)
      throw error
    }
  },

  deleteCallHandlingProfile: async (profileId: string) => {
    try {
      const { getCurrentDeviceSerialId } = useDevicesStore.getState()
      const headers = useAuthStore.getState().getApiHeaders()
      const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/profiles/${profileId}`

      await axios.delete(url, { headers })

      // Remove from local state
      const { callhandlingProfiles } = get()
      set({
        callhandlingProfiles: callhandlingProfiles.filter(p => p.id !== profileId)
      })
    } catch (error) {
      console.error('Failed to delete call handling profile:', error)
      throw error
    }
  },

  isProcessing: () => {
    const { processing, globalProcessing } = get()
    return processing || globalProcessing
  }
}))
